import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Users, Plus, Edit2, MoreHorizontal, UserX, UserCheck, Send, KeyRound } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth-context";
import { MODULES } from "@/lib/modules";
import { modulosVisiveis } from "@shared/modulos";
import { DESCRICAO_PAPEL, PAPEIS_CADASTRAVEIS } from "@shared/permissoes";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { PageHeader, ALVO_TOQUE, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { StatusBadge } from "@/components/data/status-badge";
import { LinkAcessoDialog, type LinkAcesso } from "@/components/admin/link-acesso-dialog";
import { SENHA_MINIMA } from "@shared/schema";
import { combina } from "@/lib/busca";
import { useFuncionarios } from "@/hooks/use-equipe";

// O `Select` do shadcn não aceita `value=""` num item (string vazia é o "sem
// seleção" interno dele), então "sem vínculo" precisa de um valor sentinela.
const SEM_FUNCIONARIO = "__sem_funcionario";

// Três papéis, três rótulos. O badge era um ternário `ADMIN ? "Admin" : "Campo"`
// — com ENCARREGADO passando a existir na tela, ele mostraria "Campo" para um
// encarregado.
const ROTULO_PAPEL: Record<string, string> = {
  ADMIN: "Admin",
  GESTOR: "Gestor",
  CAMPO: "Campo",
  ENCARREGADO: "Encarregado",
  LEITURA: "Leitura",
};
type User = {
  id: string;
  email: string | null;
  telefone?: string | null;
  name: string;
  role: "ADMIN" | "GESTOR" | "CAMPO" | "ENCARREGADO" | "LEITURA";
  /** Migração 052 — qual funcionário este login é. Ver o campo no formulário. */
  funcionarioId?: string | null;
  /** Migração 066 — desativado não entra. */
  ativo?: boolean;
  /** Migração 066 — restrição de módulos. `null` = todos os da org. */
  modulos?: string[] | null;
  ultimoLoginEm?: string | null;
  /** PR 3 — convidado por link e ainda sem senha própria. */
  convitePendente?: boolean;
  createdAt: string;
};

const usuarioFormSchema = z.object({
  // E-mail OU telefone: a main aceita login por telefone desde a 055 (o corretor
  // muitas vezes não tem e-mail de uso corrente). O `refine` exige um dos dois.
  email: z.string().default(""),
  telefone: z.string().default(""),
  name: z.string().min(1, "Nome é obrigatório"),
  role: z.enum(["ADMIN", "GESTOR", "ENCARREGADO", "CAMPO"]),
  password: z.string().optional().default(""),
  // Criar SEM senha é o padrão (convite por link); o checkbox liga o fallback.
  definirSenhaAgora: z.boolean().default(false),
  // "" = sem vínculo (o Select não guarda null). Vira `null` no envio.
  funcionarioId: z.string().default(""),
  // "Todos os módulos" = lista vazia + flag; vira `null` no envio.
  todosModulos: z.boolean().default(true),
  modulos: z.array(z.string()).default([]),
}).refine(d => !!d.email.trim() || !!d.telefone.trim(), {
  message: "Informe e-mail ou telefone",
  path: ["email"],
}).refine(d => !d.email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email.trim()), {
  message: "Email inválido",
  path: ["email"],
});

type UsuarioFormData = z.infer<typeof usuarioFormSchema>;

const COLUNAS_USUARIOS = [null, "15rem", "7rem", "12rem", "6rem", "9rem", "4rem"];

/** Nome do módulo pelo id, do registry — nunca uma segunda tabela de rótulos. */
function rotuloModulo(id: string): string {
  return MODULES.find(m => m.id === id)?.label ?? id;
}

/** "Todos" ou a lista curta — o que cabe num card e numa célula. */
function rotuloModulos(modulos: string[] | null | undefined): string {
  if (!modulos) return "Todos os módulos";
  if (modulos.length === 0) return "Nenhum módulo";
  return modulos.map(rotuloModulo).join(" · ");
}

/** Contato do card: e-mail se houver, senão o telefone formatado. */
function contato(u: User): string {
  if (u.email) return u.email;
  if (u.telefone) return formatarTelefone(u.telefone);
  return "—";
}

function formatarTelefone(digitos: string): string {
  const d = digitos.replace(/\D/g, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return digitos;
}

/** "Nunca" ou a data + hora do último login, no fuso do navegador. */
function formatarUltimoAcesso(iso: string | null | undefined): string {
  if (!iso) return "Nunca";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" })
    + " " + d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function BadgeStatus({ u }: { u: User }) {
  if (u.ativo === false) return <StatusBadge tom="neutro">Inativo</StatusBadge>;
  if (u.convitePendente) return <StatusBadge tom="atencao" ponto>Convidado</StatusBadge>;
  return <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>;
}

// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_BUSCA = { q: "" };

export default function UsuariosPage() {
  const { user: eu } = useAuth();
  // Os módulos que a ORG tem — as opções da restrição por usuário. Vem do
  // `modulesEnabled` cru (da org), não do efetivo: quem administra pode
  // liberar para outro um módulo que ele mesmo não vê.
  const modulosDaOrg = modulosVisiveis(eu?.modulesEnabled ?? eu?.modulos ?? ["cacho"]);
  // Lista de funcionários para o vínculo login ↔ pessoa. Só ativos: vincular a
  // quem saiu da fazenda é sempre engano.
  const { data: funcionarios = [] } = useFuncionarios();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Busca NO CLIENTE, sobre a lista já carregada.
  const { estado, definir } = useEstadoNaUrl(PADRAO_BUSCA);
  const busca = estado.q;
  const [editingUser, setEditingUser] = useState<User | null>(null);
  // O link de convite/nova senha recém-gerado, para copiar ou mandar por WhatsApp.
  const [linkGerado, setLinkGerado] = useState<{ link: LinkAcesso; titulo: string; user: { name: string; telefone?: string | null } } | null>(null);
  const { toast } = useToast();

  const { data: usuarios, isLoading } = useQuery<User[]>({
    queryKey: ["/api/usuarios"],
  });

  const visiveis = (usuarios ?? []).filter(u => combina(busca, u.name, u.email ?? "", u.telefone ?? ""));

  const form = useForm<UsuarioFormData>({
    resolver: zodResolver(usuarioFormSchema),
    defaultValues: {
      email: "",
      telefone: "",
      name: "",
      role: "CAMPO",
      password: "",
      definirSenhaAgora: false,
      funcionarioId: "",
      todosModulos: true,
      modulos: [],
    },
  });

  // ⚠️ O payload de REDE não é o do formulário: o Select guarda "" para "sem
  // vínculo", e o servidor precisa de `null` (que é o que desliga); "todos os
  // módulos" é `null`, e a lista só vai quando é restrição. A conversão acontece
  // em `handleSubmit`, e o tipo abaixo é o do que sai daqui.
  type UsuarioPayload = {
    email?: string | null;
    telefone?: string | null;
    name: string;
    role: UsuarioFormData["role"];
    password?: string;
    funcionarioId: string | null;
    modulos: string[] | null;
  };

  const fechar = () => {
    setIsDialogOpen(false);
    setEditingUser(null);
    form.reset();
  };

  const erro = (error: Error) => toast({ title: "Erro", description: error.message, variant: "destructive" });

  const createMutation = useMutation({
    mutationFn: async (data: UsuarioPayload) => apiRequest("POST", "/api/usuarios", data).then(r => r.json()),
    onSuccess: (criado: User & { convite?: LinkAcesso | null }) => {
      queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] });
      fechar();
      if (criado?.convite) {
        setLinkGerado({ link: criado.convite, titulo: "Convite criado", user: { name: criado.name, telefone: criado.telefone } });
      } else {
        toast({ title: "Usuário criado", description: "O novo usuário foi criado com sucesso" });
      }
    },
    onError: erro,
  });

  // Reenviar convite / gerar link de nova senha — o mesmo dialog de link.
  const linkMutation = useMutation({
    mutationFn: async ({ u, tipo }: { u: User; tipo: "convite" | "reset-link" }) =>
      apiRequest("POST", `/api/usuarios/${u.id}/${tipo}`).then(r => r.json()) as Promise<User & { convite: LinkAcesso }>,
    onSuccess: (r, v) => {
      queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] });
      setLinkGerado({ link: r.convite, titulo: v.tipo === "convite" ? "Convite reenviado" : "Link de nova senha", user: { name: r.name, telefone: r.telefone } });
    },
    onError: erro,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<UsuarioPayload>) => {
      if (!editingUser) throw new Error("Nenhum usuário selecionado");
      return await apiRequest("PATCH", `/api/usuarios/${editingUser.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] });
      fechar();
      toast({ title: "Usuário atualizado", description: "O usuário foi atualizado com sucesso" });
    },
    onError: erro,
  });

  // Desativar em vez de excluir: o histórico aponta para o usuário. A sessão
  // viva dele cai em ≤ 60 s (PR 1 do permissionamento).
  const ativarMutation = useMutation({
    mutationFn: async ({ id, ativar }: { id: string; ativar: boolean }) =>
      apiRequest("POST", `/api/usuarios/${id}/${ativar ? "reativar" : "desativar"}`),
    onSuccess: (_r, v) => {
      queryClient.invalidateQueries({ queryKey: ["/api/usuarios"] });
      toast({
        title: v.ativar ? "Usuário reativado" : "Usuário desativado",
        description: v.ativar ? "Ele já pode entrar de novo." : "Ele não entra mais; a sessão dele cai em até um minuto.",
      });
    },
    onError: erro,
  });

  const handleNewUser = () => {
    setEditingUser(null);
    form.reset({
      email: "",
      telefone: "",
      name: "",
      role: "CAMPO",
      password: "",
      definirSenhaAgora: false,
      funcionarioId: "",
      todosModulos: true,
      modulos: [],
    });
    setIsDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    form.reset({
      email: user.email ?? "",
      telefone: user.telefone ? formatarTelefone(user.telefone) : "",
      name: user.name,
      // LEITURA não é cadastrável (D4): um usuário assim (só via suporte) cai em CAMPO na edição.
      role: user.role === "LEITURA" ? "CAMPO" : user.role,
      password: "",
      funcionarioId: user.funcionarioId ?? "",
      todosModulos: !user.modulos,
      modulos: user.modulos ?? [],
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (data: UsuarioFormData) => {
    const modulos = data.todosModulos ? null : data.modulos;
    if (editingUser) {
      const updateData: Partial<UsuarioPayload> = {
        name: data.name,
        role: data.role,
        // Sempre enviado no update: "" vira null e DESLIGA o vínculo, que é como
        // se desfaz um vínculo errado. O servidor distingue null de ausente.
        funcionarioId: data.funcionarioId || null,
        telefone: data.telefone.trim() || null,
        modulos,
        ...(data.password?.trim() ? { password: data.password } : {}),
      };
      updateMutation.mutate(updateData);
    } else {
      // Sem "definir senha agora" o usuário nasce CONVIDADO e recebe um link.
      const comSenha = data.definirSenhaAgora;
      if (comSenha && (data.password?.trim().length ?? 0) < SENHA_MINIMA) {
        toast({ title: "Senha curta", description: `Mínimo de ${SENHA_MINIMA} caracteres.`, variant: "destructive" });
        return;
      }
      createMutation.mutate({
        email: data.email.trim() || null,
        telefone: data.telefone.trim() || null,
        name: data.name,
        role: data.role,
        ...(comSenha ? { password: data.password } : {}),
        funcionarioId: data.funcionarioId || null,
        modulos,
      });
    }
  };

  const todosModulos = form.watch("todosModulos");
  const definirSenhaAgora = form.watch("definirSenhaAgora");

  /** Menu de ações de um usuário — editar, desativar/reativar. */
  function MenuAcoes({ u, testId }: { u: User; testId: string }) {
    const souEu = u.id === eu?.id;
    const inativo = u.ativo === false;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" className={ALVO_TOQUE} aria-label={`Ações de ${u.name}`} data-testid={testId}>
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => handleEditUser(u)}>
            <Edit2 className="w-4 h-4 mr-2" />Editar
          </DropdownMenuItem>
          {!inativo && u.convitePendente && (
            <DropdownMenuItem onClick={() => linkMutation.mutate({ u, tipo: "convite" })} data-testid={`menu-reenviar-convite-${u.id}`}>
              <Send className="w-4 h-4 mr-2" />Reenviar convite
            </DropdownMenuItem>
          )}
          {!inativo && (
            <DropdownMenuItem onClick={() => linkMutation.mutate({ u, tipo: "reset-link" })} data-testid={`menu-reset-link-${u.id}`}>
              <KeyRound className="w-4 h-4 mr-2" />Gerar link de senha
            </DropdownMenuItem>
          )}
          {inativo ? (
            <DropdownMenuItem onClick={() => ativarMutation.mutate({ id: u.id, ativar: true })} data-testid={`menu-reativar-${u.id}`}>
              <UserCheck className="w-4 h-4 mr-2" />Reativar
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled={souEu}
              onClick={() => ativarMutation.mutate({ id: u.id, ativar: false })}
              className="text-destructive focus:text-destructive"
              data-testid={`menu-desativar-${u.id}`}
            >
              <UserX className="w-4 h-4 mr-2" />{souEu ? "Desativar (não a si mesmo)" : "Desativar"}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Usuários"
        descricao="Quem entra no app, com que papel e em quais módulos"
        acao={
          <Button onClick={handleNewUser} className={ALVO_TOQUE_BOTAO} data-testid="button-novo-usuario">
            <Plus className="w-4 h-4 mr-2" />
            Novo usuário
          </Button>
        }
      />

      <PageToolbar
        fim={<PageSearch valor={busca} onChange={v => definir({ q: v })} placeholder="Nome, e-mail ou telefone" testId="input-busca-usuarios" />}
      >
        <span />
      </PageToolbar>

      {isLoading ? (
        <PageSkeleton variante="tabela" linhas={4} />
      ) : !usuarios || usuarios.length === 0 ? (
        <EmptyState
          icone={Users}
          titulo="Nenhum usuário cadastrado"
          descricao="Usuário é quem entra no app — o funcionário do módulo Equipe é outra coisa, e não dá acesso."
          acao={
            <Button onClick={handleNewUser}>
              <Plus className="w-4 h-4 mr-2" />Novo usuário
            </Button>
          }
          testId="empty-usuarios"
        />
      ) : visiveis.length === 0 ? (
        <NadaEncontrado termo={busca} />
      ) : (
        <>
          {/* Celular: card por usuário. Status e módulos são METADADO — o que não
              couber vai para a edição; nome e contato nunca somem. */}
          <AdminLista>
            {visiveis.map(user => (
              <AdminListaItem
                key={user.id}
                testId={`card-usuario-${user.id}`}
                onClick={() => handleEditUser(user)}
                atenuado={user.ativo === false}
                identidade={user.name}
                meta={
                  <>
                    <span className="truncate">{contato(user)}</span>
                    <Badge variant="outline" className="text-xs font-normal" data-testid={`badge-role-${user.id}`}>
                      {ROTULO_PAPEL[user.role] ?? user.role}
                    </Badge>
                    <BadgeStatus u={user} />
                    <span>{rotuloModulos(user.modulos)}</span>
                    <span>Último acesso: {formatarUltimoAcesso(user.ultimoLoginEm)}</span>
                  </>
                }
                acoes={<MenuAcoes u={user} testId={`button-acoes-usuario-${user.id}`} />}
              />
            ))}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={COLUNAS_USUARIOS}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Nome</DataTableHead>
                  <DataTableHead>Contato</DataTableHead>
                  <DataTableHead>Papel</DataTableHead>
                  <DataTableHead>Módulos</DataTableHead>
                  <DataTableHead>Status</DataTableHead>
                  <DataTableHead>Último acesso</DataTableHead>
                  <DataTableHead />
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {visiveis.map(user => (
                  <DataTableRow key={user.id} data-testid={`row-usuario-${user.id}`} className={user.ativo === false ? "opacity-60" : undefined}>
                    {/* O nome é a coluna que responde à pergunta da tela. */}
                    <DataTableCell destaque className="truncate">{user.name}</DataTableCell>
                    <DataTableCell className="text-muted-foreground truncate">{contato(user)}</DataTableCell>
                    <DataTableCell>
                      {/* Papel é CATEGORIA, não estado: outline neutro. */}
                      <Badge variant="outline" className="text-xs font-normal" data-testid={`badge-role-${user.id}`}>
                        {ROTULO_PAPEL[user.role] ?? user.role}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell className="text-xs text-muted-foreground truncate" data-testid={`cell-modulos-${user.id}`}>
                      {rotuloModulos(user.modulos)}
                    </DataTableCell>
                    <DataTableCell data-testid={`cell-status-${user.id}`}><BadgeStatus u={user} /></DataTableCell>
                    <DataTableCell className="text-xs text-muted-foreground tabular-nums">{formatarUltimoAcesso(user.ultimoLoginEm)}</DataTableCell>
                    <DataTableCell className="text-right">
                      <MenuAcoes u={user} testId={`button-acoes-usuario-desktop-${user.id}`} />
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}

      {linkGerado && (
        <LinkAcessoDialog
          link={linkGerado.link}
          titulo={linkGerado.titulo}
          nome={linkGerado.user.name}
          telefone={linkGerado.user.telefone}
          onFechar={() => setLinkGerado(null)}
        />
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => (open ? setIsDialogOpen(true) : fechar())}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="dialog-usuario">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Editar Usuário" : "Novo Usuário"}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
              data-testid="form-usuario"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Nome completo" data-testid="input-name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="usuario@example.com"
                        disabled={!!editingUser}
                        data-testid="input-email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Telefone é o login de quem não tem e-mail (a 055 já aceita). */}
              <FormField
                control={form.control}
                name="telefone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefone (opcional)</FormLabel>
                    <FormControl>
                      <Input type="tel" inputMode="tel" placeholder="(74) 99999-8888" data-testid="input-telefone" {...field} />
                    </FormControl>
                    <FormDescription>Serve de login quando não há e-mail.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Papel como RADIO com a descrição de cada um (shared/permissoes.ts):
                  o admin escolhe sabendo o que está dando. LEITURA existe na
                  matriz (modo suporte) mas não se cadastra — D4. */}
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Papel</FormLabel>
                    <FormControl>
                      <RadioGroup value={field.value} onValueChange={field.onChange} className="gap-2" data-testid="radio-role">
                        {PAPEIS_CADASTRAVEIS.map(p => (
                          <label
                            key={p}
                            htmlFor={`role-${p}`}
                            className={
                              "flex items-start gap-3 rounded-lg border p-3 cursor-pointer " +
                              (field.value === p ? "border-primary bg-primary/5" : "")
                            }
                          >
                            <RadioGroupItem value={p} id={`role-${p}`} className="mt-0.5" data-testid={`radio-role-${p}`} />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium">{DESCRICAO_PAPEL[p].rotulo}</span>
                              <span className="block text-xs text-muted-foreground">{DESCRICAO_PAPEL[p].resumo}</span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Módulos que ESTE usuário vê (migração 066). Só os que a org tem;
                  "Todos" = null no servidor, que é o estado de todo mundo hoje. */}
              <FormField
                control={form.control}
                name="todosModulos"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Módulos</FormLabel>
                    <div className="flex flex-row items-center space-x-2 mt-2">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={v => field.onChange(!!v)} data-testid="checkbox-todos-modulos" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">Todos os módulos da fazenda</FormLabel>
                    </div>
                    {!todosModulos && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {modulosDaOrg.map(id => (
                          <FormField
                            key={id}
                            control={form.control}
                            name="modulos"
                            render={({ field: f }) => {
                              const marcado = f.value?.includes(id);
                              return (
                                <button
                                  type="button"
                                  onClick={() => f.onChange(marcado ? (f.value ?? []).filter(m => m !== id) : [...(f.value ?? []), id])}
                                  data-testid={`chip-modulo-${id}`}
                                  aria-pressed={marcado}
                                  className={
                                    "h-9 px-3 rounded-full border text-sm transition-colors " +
                                    (marcado ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground")
                                  }
                                >
                                  {rotuloModulo(id)}
                                </button>
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                    <FormDescription>Restringe o que aparece no seletor e o que a API aceita.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Vínculo login ↔ pessoa (migração 052). É o que faz "Só as minhas"
                  existir na tela Hoje do Manejo e o que liga a conclusão de uma
                  operação ao funcionário certo. Opcional: sem vínculo o operador
                  vê todas as operações do dia, que é o comportamento de hoje. */}
              <FormField
                control={form.control}
                name="funcionarioId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Funcionário</FormLabel>
                    <Select value={field.value || SEM_FUNCIONARIO} onValueChange={v => field.onChange(v === SEM_FUNCIONARIO ? "" : v)}>
                      <FormControl>
                        <SelectTrigger data-testid="select-funcionario">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={SEM_FUNCIONARIO}>Sem vínculo</SelectItem>
                        {funcionarios.map(f => (
                          <SelectItem key={f.id} value={f.id}>{f.apelido ?? f.nome}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Liga este login à pessoa nas operações do Manejo. Cada funcionário
                      pertence a um login só.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!editingUser && (
                <FormField
                  control={form.control}
                  name="definirSenhaAgora"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={v => field.onChange(!!v)} data-testid="checkbox-definir-senha-agora" />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer">Definir a senha agora (em vez de mandar o link)</FormLabel>
                    </FormItem>
                  )}
                />
              )}

              {(editingUser || definirSenhaAgora) && (
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {editingUser ? "Nova Senha (deixar em branco para manter)" : "Senha"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="••••••••"
                        data-testid="input-password"
                        {...field}
                      />
                    </FormControl>
                    {editingUser && (
                      <FormDescription>Trocar a senha encerra a sessão atual dele.</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              )}

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={fechar}
                  className="flex-1"
                  data-testid="button-cancel"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  data-testid="button-save"
                >
                  {editingUser ? "Atualizar" : "Criar"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
